import React, { useState } from "react";
import {
  Button,
  Dialog,
  Card,
  CardBody,
  CardFooter,
  Typography,
  Input,
} from "@material-tailwind/react";
import { useGlobalData } from "../Layout/GlobalContext";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { useUserStore } from "@/store/user";
import { ForgotPasswordDialog } from "./ForgotPasswordDialog";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\d{6,15}$/;

/**
 * 前端轻量识别账号类型，仅用于提交前的"用户友好"校验。
 * 真正的判定与分流在后端 accountPassword 完成，前端宽松一些不影响安全。
 */
const detectAccountType = (v: string): "email" | "phone" | "invalid" => {
  const value = v.trim();
  if (!value) return "invalid";
  if (EMAIL_RE.test(value)) return "email";
  if (PHONE_RE.test(value)) return "phone";
  return "invalid";
};

/**
 * 登录 Dialog（统一入口）
 *
 * 一个输入框接收 邮箱 / 手机号，后端 `/account-password` 按格式自动分流：
 *  - 含 @ → 走 emailPassword
 *  - 纯数字 → 走 phonePassword（透传 authCode）
 *
 * 找回密码统一基于邮箱验证码（见 ForgotPasswordDialog）。
 */
export function LoginDialog({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) {
  const { setLoginModal, setRegisterModal, loginByAccount, alert } =
    useGlobalData();
  const { authCode } = useUserStore();

  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [submit, setSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const accountType = detectAccountType(account);
  const accountInvalid = submit && accountType === "invalid";

  const resetForm = () => {
    setAccount("");
    setPassword("");
    setSubmit(false);
  };

  const handleLogin = async () => {
    setSubmit(true);
    if (accountType === "invalid") {
      alert({ color: "red", message: "请输入有效的邮箱或手机号" });
      return;
    }
    if (!password) {
      alert({ color: "red", message: "请输入密码" });
      return;
    }
    setLoading(true);
    try {
      // authCode 仅手机号分支会被后端使用；邮箱分支被忽略，无副作用
      await loginByAccount(account.trim(), password, authCode);
      alert({ color: "green", message: "登录成功" });
      setLoginModal(false);
      resetForm();
    } catch (error) {
      // loginByAccount 内部已 alert
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        size="md"
        open={open}
        handler={() => setLoginModal(true)}
        className="bg-transparent shadow-none"
      >
        <Card className="mx-auto w-full md:max-w-[24rem]">
          <CardBody className="flex flex-col gap-4">
            <XCircleIcon
              className="absolute w-7 right-5 top-5 cursor-pointer"
              onClick={handleClose}
            />
            <Typography variant="h4" color="blue-gray">
              登录
            </Typography>

            <Typography className="-mb-2" variant="h6">
              账号
            </Typography>
            <Input
              error={accountInvalid}
              type="text"
              value={account}
              onChange={({ target }) => setAccount(target.value)}
              crossOrigin=""
              label="邮箱 / 手机号"
              size="lg"
              autoComplete="username"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleLogin();
              }}
            />

            <Typography className="-mb-2" variant="h6">
              密码
            </Typography>
            <Input
              error={submit && !password}
              type="password"
              value={password}
              onChange={({ target }) => setPassword(target.value)}
              crossOrigin=""
              label="密码"
              size="lg"
              autoComplete="current-password"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleLogin();
              }}
            />

            <div className="ml-1 flex justify-end items-center">
              <Typography
                as="a"
                variant="small"
                color="black"
                className="cursor-pointer hover:underline"
                onClick={() => {
                  handleClose();
                  setForgotPasswordOpen(true);
                }}
              >
                忘记密码？（通过邮箱找回）
              </Typography>
            </div>
          </CardBody>
          <CardFooter className="pt-0">
            <Button
              variant="gradient"
              onClick={handleLogin}
              fullWidth
              loading={loading}
              disabled={loading}
            >
              登录
            </Button>
            <Typography variant="small" className="mt-4 flex justify-center">
              还没有账号？
              <Typography
                as="a"
                variant="small"
                color="blue-gray"
                className="ml-1 font-bold cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleClose();
                  setRegisterModal(true);
                }}
              >
                邮箱注册
              </Typography>
            </Typography>
          </CardFooter>
        </Card>
      </Dialog>

      <ForgotPasswordDialog
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
      />
    </>
  );
}

export const NoLogin: React.FC<{
  login?: () => void;
}> = ({ login }) => {
  const Global = useGlobalData();
  return (
    <div className="flex flex-col justify-center items-center">
      <img
        className="w-80 h-52"
        src="https://cool-jiaojq.oss-cn-beijing.aliyuncs.com/app%2Fbase%2F8f0b95d1ec874a3187067f4cdd64bd40_nologin.png"
        alt=""
      />
      <Button
        onClick={() => {
          Global.handleRegister({
            onSuccess() {
              login && login();
            },
          });
        }}
        className="mt-5"
      >
        登录查看
      </Button>
    </div>
  );
};
